import React, { FC } from 'react';
import styled, { css } from 'styled-components/macro';

import Button, { ButtonProps } from 'components/Button';
import media from 'helpers/media';

const Wrapper = styled.div<{
    fullWidth?: boolean;
    align: 'left' | 'center' | 'right' | 'inherit';
}>`
    text-align: ${({ align }) => align};

    &:not(:first-child) {
        margin-top: ${({ theme }) => theme.space.xlarge};
    }

    .Button_styled {
        margin: 0 ${({ theme }) => theme.space.small};
    }

    ${({ theme }) => media.mobile`
        display: flex;
        flex-direction: column;
        margin-left: auto;
        margin-right: auto;
        &:not(:first-child) {
            margin-top: ${theme.space.medium};
        }

        .Button_styled {
            margin-bottom: ${theme.space.base};
            &:last-child {
                margin-bottom: 0;
            }
        }
    `}

    ${({ fullWidth }) =>
        fullWidth &&
        css`
            width: 100%;
            box-sizing: border-box;
        `};
`;

export interface ActionsProps {
    /** Alignment of the buttons */
    align: 'left' | 'center' | 'right' | 'inherit';

    /** Primary button as an object */
    primary: ButtonProps;

    /** Secondary button as an object */
    secondary?: ButtonProps;

    fullWidth?: boolean;
}

/**
 *
 * The <strong>Actions</strong> component can be used to
 * display a row of <strong>CTAs</strong> the user can choose from.
 * The component should only be used to display <strong>button components</strong>
 * and the number of buttons must be <strong>limited to two</strong>.
 *
 *
 * The space between <strong>Actions</strong> and any <strong>other component</strong> above,
 * will automatically be set. On mobile devices the <code>margin-top</code> is
 * going to be <code>16px</code> but it'll increase to
 * <code>48px</code> from tablet to desktop.
 *
 *
 * When two actions are being displayed, the one on the left will always be a <i>Secondary CTA</i>.
 *
 *
 */
const Actions: FC<ActionsProps & React.HTMLAttributes<HTMLDivElement>> = ({
    align = 'center',
    primary,
    secondary,
    fullWidth,
    ...rest
}) => {
    return (
        <Wrapper {...rest} align={align} fullWidth={fullWidth}>
            {secondary && <Button {...secondary} className="Button_styled" />}
            {primary && <Button {...primary} className="Button_styled" />}
        </Wrapper>
    );
};

export default Actions;
